import type {
  CollectionReference,
  PartialWithFieldValue,
  Query,
} from 'firebase/firestore'
import {
  and,
  collection,
  collectionGroup,
  doc,
  query,
  serverTimestamp,
  where,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { modelListStream } from '../../firestore-mobx/stream'
import { StaticModelCollection } from '../../firestore-mobx/model'
import { addDocWithError, deleteDocWithError } from '../../firestore-mobx/fetch'
import type { FirestoreRoomStateUPRNote } from './schema'
import { empty, schema, writeSchema } from './schema'
import { RoomStateUPRNote } from '../../models/RoomStateUPRNote'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreRoomStateUPRNote> = {
  toFirestore: (data: PartialWithFieldValue<FirestoreRoomStateUPRNote>) => {
    writeSchema.partial().parse(data)
    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

export const getColGroupRef = (
  firestore: Firestore
): Query<FirestoreRoomStateUPRNote> => {
  return collectionGroup(firestore, 'peer_review_note').withConverter(converter)
}

export const getColRef = (
  firestore: Firestore,
  roomId: string,
  userId: string,
  reviewerUserId: string
): CollectionReference<FirestoreRoomStateUPRNote> => {
  return collection(
    firestore,
    'room_state',
    roomId,
    'user_peer_review',
    userId,
    'peer_review',
    reviewerUserId,
    'peer_review_note'
  ).withConverter(converter)
}

export const buildEmptyRoomStateUPRNoteCollection = (
  repository: FirebaseRepository
) => {
  return new StaticModelCollection({
    repository,
    model: RoomStateUPRNote,
    empty,
  })
}

export const upsertPeerReviewNote = async (
  repository: FirebaseRepository,
  params: {
    roomId: string
    sectionId: string
    assignmentId: string
    userId: string
    reviewerUserId: string
    reviewNote: string
    sharedWithPeer: boolean
  }
) => {
  const { firestore } = repository
  const {
    roomId,
    sectionId,
    assignmentId,
    userId,
    reviewerUserId,
    reviewNote,
    sharedWithPeer,
  } = params

  const colRef = getColRef(firestore, roomId, userId, reviewerUserId)
  const payload: FirestoreRoomStateUPRNote = {
    roomId,
    sectionId,
    assignmentId,
    userId,
    reviewerUserId,
    reviewNote,
    sharedWithPeer,
    updatedAt: serverTimestamp() as unknown as Date,
  }
  return addDocWithError(colRef, payload, 'UpsertPeerReviewNoteError')
}

export const deletePeerReviewNote = async (
  repository: FirebaseRepository,
  params: {
    roomId: string
    userId: string
    reviewerUserId: string
    noteId: string
  }
) => {
  const { firestore } = repository
  const { roomId, userId, reviewerUserId, noteId } = params
  const colRef = getColRef(firestore, roomId, userId, reviewerUserId)
  const noteRef = doc(colRef, noteId)
  return deleteDocWithError(noteRef, 'DeletePeerReviewNoteError')
}

export const getPeerReviewNotesForReviewerInRoom = (
  repository: FirebaseRepository,
  { roomId, reviewerUserId }: { roomId: string; reviewerUserId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(
      where('roomId', '==', roomId),
      where('reviewerUserId', '==', reviewerUserId)
    )
  )
  return modelListStream(repository, q, RoomStateUPRNote)
}

export const getPeerReviewNotesForRevieweeInRoom = (
  repository: FirebaseRepository,
  { roomId, userId }: { roomId: string; userId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(
      where('roomId', '==', roomId),
      where('userId', '==', userId),
      where('sharedWithPeer', '==', true)
    )
  )
  return modelListStream(repository, q, RoomStateUPRNote)
}

export const getPeerReviewNotesForUserInRoom = (
  repository: FirebaseRepository,
  {
    roomId,
    userId,
    reviewerUserId,
  }: {
    roomId: string
    userId: string
    reviewerUserId: string
  }
) => {
  const colRef = getColRef(repository.firestore, roomId, userId, reviewerUserId)
  return modelListStream(repository, colRef, RoomStateUPRNote)
}

export const getPeerReviewNotesForAssignment = (
  repository: FirebaseRepository,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(
      where('sectionId', '==', sectionId),
      where('assignmentId', '==', assignmentId)
    )
  )
  return modelListStream(repository, q, RoomStateUPRNote)
}
